import ApiHelper from "../../../common/utils/apiHelper";
import type { TransportAirlineApiContext } from "../../../common/utils/transport/airlineTransportContext";
import { buildAirlineMilesAndMoreBookingFields } from "../../../common/utils/transport/airlineBookingPayload";
import {
  buildDestinationTransportPayload,
  postDestinationTransportEstimation
} from "../../../common/utils/transport/destinationTransportEstimation";
import { globalActionCreators, selectApiBaseUrl } from "../../../common/store/modules/globalModule";
import { confirmationActionCreators } from "../../../common/store/modules/bookingConfirmationModule";
import { isEmailInvalid } from "../../../common/utils/helpers/isEmailInvalid";
import { checkForEmptyField } from "../../../common/utils/helpers/checkForEmptyFields";
import { handleErrorAlert } from "../../../common/utils/helpers/handleErrorAlert";
import { resolveObbApiPaths } from "./helpers/resolveObbApiPaths";
import { mapObbPassengersForApi } from "./helpers/mapObbPassengersForApi";
import { createInitialObbPassenger } from "./createInitialObbPassenger";

const USE_MOCK_OBB_INFO = false;
const USE_MOCK_OBB_STATIONS = false;

const MOCK_OBB_STATIONS_RESPONSE = [
  {
    station_code: "ATWIEHBF",
    country_code: "AT",
    name: "Wien Hbf",
    station_type: "train_station",
    uic_station_code: "8103000",
    time_zone: "Europe/Vienna",
    location: { lat: 48.185, lon: 16.372 },
    latitude: 48.185,
    longitude: 16.372
  },
  {
    station_code: "ATVIE",
    country_code: "AT",
    name: "Wien Flughafen",
    station_type: "train_station",
    uic_station_code: "8101508",
    time_zone: "Europe/Vienna",
    location: { lat: 48.121, lon: 16.563 },
    latitude: 48.121,
    longitude: 16.563
  },
  {
    station_code: "ATWIEWBF",
    country_code: "AT",
    name: "Wien Westbahnhof",
    station_type: "train_station",
    uic_station_code: "8100515",
    time_zone: "Europe/Vienna",
    location: { lat: 48.197, lon: 16.337 },
    latitude: 48.197,
    longitude: 16.337
  },
  {
    station_code: "ATGRZFBG",
    country_code: "AT",
    name: "Flughafen Graz-Feldkirchen Train Station",
    station_type: "train_station",
    uic_station_code: "8103176",
    street_and_number: "Flughafenstraße 220",
    zip_code: "8073",
    time_zone: "Europe/Vienna",
    location: { lat: 46.99764, lon: 15.44964 },
    latitude: 46.99764,
    longitude: 15.44964
  }
];

const MOCK_OBB_INFO_RESPONSE = {
  provider_title: "Austrian Public Transport",
  provider_description:
    "Book ÖBB public transport tickets for your journey across Austria. Select your route, passengers and travel class to see available options.",
  provider_logo: {
    url: "https://d1o0qt6ld1319s.cloudfront.net/dev/zurich/390c0357-516c-45ae-91b6-152fb9996817/65249b87c14fafd.png",
    name: "OBB"
  },
  name: "Austrian Public Transport",
  type: "OBB",
  main: "ECKWOBBQ",
  logo: {
    url: "https://d1o0qt6ld1319s.cloudfront.net/dev/zurich/b683d21a-0759-44c1-aa77-7ef141ec3f7b/78963d891e5df95.png",
    name: "Österreichische Bundesbahnen (ÖBB)"
  },
  provider_cover: {
    url: "https://d1o0qt6ld1319s.cloudfront.net/dev/zurich/b683d21a-0759-44c1-aa77-7ef141ec3f7b/fd24851606ab2cc-332x118.png",
    name: "OBB"
  },
  details: {
    custom_route_offer_code: "ECKWOBBQ"
  }
};

export const obbActionTypes = {
  CALL_OBB_API: "CALL_OBB_API",
  TOGGLE_OBB_INFO_ERROR: "TOGGLE_OBB_INFO_ERROR",
  UPDATE_PASSENGERS_LIST: "UPDATE_OBB_PASSENGERS_LIST",
  SET_PREBOOK_INFO_PRICE: "SET_OBB_PREBOOK_INFO_PRICE",
  UPDATE_DATA_FOR_BOOKING: "UPDATE_OBB_DATA_FOR_BOOKING",
  UPDATE_PREBOOK_API_ERROR: "UPDATE_OBB_PREBOOK_API_ERROR",
  SET_OBB_BOOKING_RESPONSE_DATA: "SET_OBB_BOOKING_RESPONSE_DATA",
  TOGGLE_OBB_BOOKING_ERROR: "TOGGLE_OBB_BOOKING_ERROR",
  TOGGLE_OBB_PAYMENT_MODAL: "TOGGLE_OBB_PAYMENT_MODAL",
  RESET_OBB_STATE: "RESET_OBB_STATE",
  SEARCH_OBB_STATIONS: "SEARCH_OBB_STATIONS",
  OBB_LAST_MILE_ROUTE_LOADING: "OBB_LAST_MILE_ROUTE_LOADING",
  OBB_LAST_MILE_ROUTE_SUCCESS: "OBB_LAST_MILE_ROUTE_SUCCESS",
  OBB_LAST_MILE_ROUTE_ERROR: "OBB_LAST_MILE_ROUTE_ERROR"
};

export const initialState = {
  obbData: null,
  obbCustomRouteCode: null,
  obbInfoError: false,
  passengerList: [createInitialObbPassenger()],
  prebookPriceInfo: null,
  errorPrebook: null,
  dataForBooking: {
    commentForm: "",
    emailForm: "",
    acceptTerms: false
  },
  obbBookingResponseData: null,
  obbBookingError: false,
  showObbPaymentModal: false,
  selectedStationFrom: null,
  selectedStationTo: null,
  lastMileRoute: null as null | {
    distance_meters?: number | null;
    duration_seconds?: number | null;
    travel_mode?: string | null;
    google_maps_url?: string | null;
  },
  isLastMileRouteLoading: false,
  lastMileRouteError: null as string | null
};

const obbModule = (state = initialState, action: any) => {
  switch (action.type) {
    case obbActionTypes.CALL_OBB_API:
      return {
        ...state,
        obbData: action.obbData,
        obbCustomRouteCode: action.obbCustomRouteCode
      };
    case obbActionTypes.TOGGLE_OBB_INFO_ERROR:
      return {
        ...state,
        obbInfoError: action.obbInfoError
      };
    case obbActionTypes.UPDATE_PASSENGERS_LIST:
      return {
        ...state,
        passengerList: action.passengerList
      };
    case obbActionTypes.SET_PREBOOK_INFO_PRICE:
      return {
        ...state,
        prebookPriceInfo: action.prebookPriceInfo
      };
    case obbActionTypes.UPDATE_DATA_FOR_BOOKING:
      return {
        ...state,
        dataForBooking: action.dataForBooking
      };
    case obbActionTypes.UPDATE_PREBOOK_API_ERROR:
      return {
        ...state,
        errorPrebook: action.errorPrebook
      };
    case obbActionTypes.SET_OBB_BOOKING_RESPONSE_DATA:
      return {
        ...state,
        obbBookingResponseData: action.obbBookingResponseData
      };
    case obbActionTypes.TOGGLE_OBB_BOOKING_ERROR:
      return {
        ...state,
        obbBookingError: action.obbBookingError
      };
    case obbActionTypes.TOGGLE_OBB_PAYMENT_MODAL:
      return {
        ...state,
        showObbPaymentModal: action.showObbPaymentModal
      };
    case obbActionTypes.RESET_OBB_STATE:
      return {
        ...state,
        ...initialState
      };
    case obbActionTypes.SEARCH_OBB_STATIONS:
      return {
        ...state,
        selectedStationFrom: action.selectedStationFrom,
        selectedStationTo: action.selectedStationTo
      };
    case obbActionTypes.OBB_LAST_MILE_ROUTE_LOADING:
      return {
        ...state,
        isLastMileRouteLoading: Boolean(action.isLoading),
        ...(action.isLoading
          ? {
              lastMileRouteError: null
            }
          : {})
      };
    case obbActionTypes.OBB_LAST_MILE_ROUTE_SUCCESS:
      return {
        ...state,
        isLastMileRouteLoading: false,
        lastMileRouteError: null,
        lastMileRoute: action.lastMileRoute ?? null
      };
    case obbActionTypes.OBB_LAST_MILE_ROUTE_ERROR:
      return {
        ...state,
        isLastMileRouteLoading: false,
        lastMileRoute: null,
        lastMileRouteError: action.error ?? "transport.lastMileError"
      };

    default:
      return state;
  }
};

export default obbModule;

export const obbActionCreators = {
  getObbInfoAction: (transportContext: TransportAirlineApiContext) => async (dispatch: any, getState: any) => {
    const apiUrl = selectApiBaseUrl(getState());
    const paths = resolveObbApiPaths(transportContext);

    dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));
    dispatch({
      type: obbActionTypes.TOGGLE_OBB_INFO_ERROR,
      obbInfoError: false
    });

    if (paths.kind === "invalid_airline") {
      dispatch({
        type: obbActionTypes.TOGGLE_OBB_INFO_ERROR,
        obbInfoError: true
      });
      dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      return;
    }

    try {
      let data: any;

      if (USE_MOCK_OBB_INFO) {
        await new Promise(resolve => setTimeout(resolve, 300));
        data = MOCK_OBB_INFO_RESPONSE;
      } else {
        const res = await ApiHelper.get(`${apiUrl}/${paths.infoPath}`);
        data = res?.data;
      }

      dispatch({
        type: obbActionTypes.CALL_OBB_API,
        obbCustomRouteCode: data?.main || data?.details?.custom_route_offer_code || null,
        obbData: data || null
      });
    } catch (err: any) {
      dispatch({
        type: obbActionTypes.TOGGLE_OBB_INFO_ERROR,
        obbInfoError: true
      });
    } finally {
      dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
    }
  },
  searchObbStationsAction:
    (query: string, direction: string, transportContext: TransportAirlineApiContext) =>
    async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState());
      const paths = resolveObbApiPaths(transportContext);
      const selectedStationFrom = getState().obb.selectedStationFrom;
      const selectedStationTo = getState().obb.selectedStationTo;

      if (paths.kind === "invalid_airline") {
        return;
      }

      if (USE_MOCK_OBB_STATIONS) {
        await new Promise(resolve => setTimeout(resolve, 300));
        const data = MOCK_OBB_STATIONS_RESPONSE;

        if (direction === "from") {
          dispatch({
            type: obbActionTypes.SEARCH_OBB_STATIONS,
            selectedStationFrom: data,
            selectedStationTo: selectedStationTo
          });
        }
        if (direction === "to") {
          dispatch({
            type: obbActionTypes.SEARCH_OBB_STATIONS,
            selectedStationTo: data,
            selectedStationFrom: selectedStationFrom
          });
        }
        return data;
      }

      if (query?.length > 2 && apiUrl) {
        try {
          const res = await ApiHelper.get(paths.stationsSearchUrl(apiUrl, query));

          if (direction === "from") {
            dispatch({
              type: obbActionTypes.SEARCH_OBB_STATIONS,
              selectedStationFrom: res?.data,
              selectedStationTo: selectedStationTo
            });
          }
          if (direction === "to") {
            dispatch({
              type: obbActionTypes.SEARCH_OBB_STATIONS,
              selectedStationTo: res?.data,
              selectedStationFrom: selectedStationFrom
            });
          }
          return res?.data;
        } catch (err: any) {}
      }
    },
  getObbLastMileRouteAction:
    ({
      departureLatitude,
      departureLongitude,
      destinationPlaceId,
      travelMode
    }: {
      departureLatitude: number;
      departureLongitude: number;
      destinationPlaceId: string;
      travelMode?: "walking" | "driving" | "transit" | "bicycling" | string;
    }) =>
    async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState());
      const safePlaceId = typeof destinationPlaceId === "string" ? destinationPlaceId.trim() : "";

      if (!apiUrl || !safePlaceId) {
        return;
      }

      dispatch({
        type: obbActionTypes.OBB_LAST_MILE_ROUTE_LOADING,
        isLoading: true
      });

      try {
        const query = [
          `departureLatitude=${encodeURIComponent(String(departureLatitude))}`,
          `departureLongitude=${encodeURIComponent(String(departureLongitude))}`,
          `destinationPlaceId=${encodeURIComponent(safePlaceId)}`,
          `travelMode=${encodeURIComponent(travelMode || "walking")}`
        ].join("&");
        const url = `${apiUrl}/v1/airports/last-mile-route?${query}`;
        const res = await ApiHelper.get(url);

        dispatch({
          type: obbActionTypes.OBB_LAST_MILE_ROUTE_SUCCESS,
          lastMileRoute: res?.data ?? null
        });
        return res?.data;
      } catch (err: any) {
        dispatch({
          type: obbActionTypes.OBB_LAST_MILE_ROUTE_ERROR,
          error: "transport.lastMileError"
        });
      } finally {
        dispatch({
          type: obbActionTypes.OBB_LAST_MILE_ROUTE_LOADING,
          isLoading: false
        });
      }
    },
  updatePassengersAction: (passengerList: Array<any>) => (dispatch: any) => {
    dispatch({
      type: obbActionTypes.UPDATE_PASSENGERS_LIST,
      passengerList
    });
  },
  getPriceObbPrebookAction:
    (params: any, transportContext: TransportAirlineApiContext) => async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState()) || "";
      const paths = resolveObbApiPaths(transportContext);
      const passengerListState = getState().obb.passengerList;

      if (paths.kind === "invalid_airline") {
        return;
      }

      const rawStartDate = typeof params?.start_date === "string" ? params.start_date : "";
      const [datePart, timePart] = rawStartDate.split(" ");

      const details: Record<string, any> = {
        passengers: mapObbPassengersForApi(passengerListState),
        qualityOfService: params?.qualityOfService,
        direction: params?.direction,
        departure: params?.origin ?? null,
        destination: params?.destination ?? null
      };

      const basePayload = buildDestinationTransportPayload({
        date: datePart || "",
        time: timePart || "",
        departureAddress: params?.departureAddress ?? null,
        departureAddressId: params?.departureAddressId ?? null,
        destinationAddress: params?.destinationAddress ?? null,
        destinationAddressId: params?.destinationAddressId ?? null,
        milesAndMore: params?.milesAndMore ?? "",
        offerCode: params?.offer_code,
        details
      });

      const apiParams: Record<string, any> = {
        ...basePayload,
        attributes: Array.isArray(params?.attributes) ? params.attributes : []
      };

      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));
      dispatch({
        type: obbActionTypes.UPDATE_PREBOOK_API_ERROR,
        errorPrebook: null
      });

      try {
        const resData = await postDestinationTransportEstimation({
          apiUrl,
          transportContext,
          payload: apiParams,
          v2: { path: paths.estimationV2Path },
          airline: { path: paths.estimationAirlinePath }
        });
        dispatch({
          type: obbActionTypes.SET_PREBOOK_INFO_PRICE,
          prebookPriceInfo: resData
        });
        return resData;
      } catch (err: any) {
        dispatch({
          type: obbActionTypes.SET_PREBOOK_INFO_PRICE,
          prebookPriceInfo: null
        });
        dispatch({
          type: obbActionTypes.UPDATE_PREBOOK_API_ERROR,
          errorPrebook: err?.response?.data
        });
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    },
  resetPrebookDataInfoAction: () => (dispatch: any) => {
    dispatch({
      type: obbActionTypes.SET_PREBOOK_INFO_PRICE,
      prebookPriceInfo: null
    });
  },
  updateFormForBookingAction:
    (dataForBooking: { commentForm: string; emailForm: string; acceptTerms?: boolean }) => (dispatch: any) => {
      dispatch({
        type: obbActionTypes.UPDATE_DATA_FOR_BOOKING,
        dataForBooking: dataForBooking
      });
    },
  toggleObbBookingErrorAction: (obbBookingError: boolean) => (dispatch: any) => {
    dispatch({
      type: obbActionTypes.TOGGLE_OBB_BOOKING_ERROR,
      obbBookingError
    });
  },
  buyObbTicketAction:
    (params: any, navigate: any, transportContext: TransportAirlineApiContext) =>
    async (dispatch: any, getState: any) => {
      const dataForBookingState = getState().obb.dataForBooking;
      const passengerListState = getState().obb.passengerList;
      const prebookPriceInfoState = getState().obb.prebookPriceInfo;
      const paths = resolveObbApiPaths(transportContext);

      localStorage.removeItem("bookingResponseDataStorage");

      let errorField = {
        emailForm: false,
        commentForm: false,
        acceptTerms: false
      };
      let errorCheck = true;

      if (isEmailInvalid(dataForBookingState.emailForm) || checkForEmptyField(dataForBookingState.emailForm)) {
        errorField.emailForm = true;
        errorCheck = false;
      }

      if (dataForBookingState?.commentForm !== "" && dataForBookingState?.commentForm?.length < 3) {
        errorField.commentForm = true;
        errorCheck = false;
      }

      if (paths.kind === "invalid_airline") {
        dispatch(obbActionCreators?.toggleObbBookingErrorAction(true));
        return;
      }

      if (!errorCheck) {
        dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: true, errorField: errorField });
      } else {
        const apiUrl = selectApiBaseUrl(getState());
        const language = localStorage.getItem("i18nextLng");

        dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));
        dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: false, errorField: errorField });

        const apiParams = {
          summary: {
            start_date: params?.start_date,
            adult: passengerListState?.length,
            timeslot: params?.timeslot,
            offer_code: params?.offer_code
          },
          personal_information: {
            first_name: passengerListState[0]?.first_name,
            last_name: passengerListState[0]?.last_name,
            email: dataForBookingState?.emailForm,
            phone: "",
            comment: dataForBookingState?.commentForm ? dataForBookingState?.commentForm : null
          },
          ...buildAirlineMilesAndMoreBookingFields(transportContext),
          hotel_stay_code: getState().tenant.tenantInfo?.code,
          session_id: prebookPriceInfoState?.details?.session_id,
          extraBookingData: {
            offer_id: prebookPriceInfoState?.details?.offer_id
          }
        };

        try {
          const response = await ApiHelper.post(`${apiUrl}/${paths.bookingsPath}`, {}, apiParams);

          localStorage.setItem("bookingResponseDataStorage", JSON.stringify(response.data));

          if (response.data?.booking_status === "error") {
            dispatch(obbActionCreators?.toggleObbBookingErrorAction(true));
            return;
          }

          dispatch(obbActionCreators?.toggleObbBookingErrorAction(false));
          dispatch({
            type: obbActionTypes.SET_OBB_BOOKING_RESPONSE_DATA,
            obbBookingResponseData: response.data
          });

          if (response.data?.payment_status === "paid") {
            await dispatch(confirmationActionCreators?.getBookingStatusAction());
            await navigate(`/${language}/transport/obb/confirmation`, { replace: true });
          } else {
            dispatch(obbActionCreators?.toggleObbPaymentModalAction(true));
          }
        } catch (error: any) {
          handleErrorAlert(error);
          dispatch(obbActionCreators?.toggleObbBookingErrorAction(true));
        } finally {
          dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
        }
      }
    },
  toggleObbPaymentModalAction: (showObbPaymentModal: boolean) => (dispatch: any) => {
    dispatch({
      type: obbActionTypes.TOGGLE_OBB_PAYMENT_MODAL,
      showObbPaymentModal
    });
  },
  resetObbStateAction: () => (dispatch: any) => {
    dispatch({
      type: obbActionTypes.RESET_OBB_STATE
    });
  }
};
